import { DataTable, DataTablePagination } from "@/src/components/ui/data-table";
import { useEffect, useState } from "react";
import { useFilters, usePageSize, usePageSort, useSearch } from "../state";
import { useNavigate } from "react-router-dom";
import { CustomerPayment, PayerType } from "../model";
import { customersColumns } from "./customersColumns";
import { paymentsFetch } from "@/src/lib/paymentsFetch";

type CustomerPaymentData = {
  data: CustomerPayment[];
} & DataTablePagination;

export function CustomersTable({ shouldRefetch }: { shouldRefetch: boolean }) {
  const [customerPayments, setCustomerPayments] = useState<CustomerPaymentData>({
    data: [],
    total: 1,
    pagination: { page: 1, pageSize: 1 },
  });
  const [page, setPage] = useState(1);

  const navigate = useNavigate();

  const search = useSearch();
  const pageSize = usePageSize();
  const { customerSortBy } = usePageSort();
  const { customerId, operatorId, amountFrom, amountTo, amountCurrency, bookingRef, operatorRef } = useFilters();

  // Construct the query string with all filters and pagination
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: pageSize.toString(),
    orderBy: customerSortBy,
    payerType: PayerType.CUSTOMER,
  });

  // Append optional filters if available
  if (customerId) queryParams.append("customerId", customerId.toString());
  if (operatorId) queryParams.append("operatorId", operatorId.toString());
  if (amountFrom) queryParams.append("amountFrom", amountFrom.toString());
  if (amountTo) queryParams.append("amountTo", amountTo.toString());
  if (amountCurrency) queryParams.append("amountCurrency", amountCurrency.toString());
  queryParams.append("bookingRef", bookingRef ? bookingRef.toString() : "null");
  queryParams.append("operatorRef", operatorRef ? operatorRef.toString() : "null");

  // Final query string
  const queryString = queryParams.toString();

  const fetchCustomersPayments = async () => {
    try {
      return await paymentsFetch<{ data: CustomerPayment[] } & DataTablePagination>(
        `/api/payment-schedule-step?${queryString}`,
        "GET",
        undefined,
        undefined,
      );
    } catch (error) {
      console.error("Error fetching customer payments", error);
    }
  };

  useEffect(() => {
    if (page > 1) {
      setPage(1);
    } else {
      fetchCustomersPayments().then((data) => {
        if (data) {
          setCustomerPayments(data);
        } else {
          setCustomerPayments({
            data: [],
            total: 0,
            pagination: { page: 1, pageSize: 10 },
          });
        }
      });
    }
  }, [
    search,
    pageSize,
    customerSortBy,
    customerId,
    operatorId,
    amountFrom,
    amountTo,
    amountCurrency,
    bookingRef,
    operatorRef,
    shouldRefetch,
  ]);

  useEffect(() => {
    fetchCustomersPayments().then((data) => {
      if (data) {
        setCustomerPayments(data);
      } else {
        setCustomerPayments({
          data: [],
          total: 0,
          pagination: { page: 1, pageSize: 10 },
        });
      }
    });
  }, [page]);

  return (
    <div className="rounded-md bg-white border">
      <DataTable
        columns={customersColumns}
        data={customerPayments?.data}
        pagination={customerPayments}
        onPageClick={(page) => setPage(page)}
        onRowClick={(r) => navigate(`/bookings/${r.booking.id}?tab=payments`)}
      />
    </div>
  );
}
